import {getSphereById} from './sphereDataHelper';
import HOTSPOT from './constants/hotspotConstants';
import EdapEventMethods from '../../src/modules/EdapEventMethods';
import {Euler, Vector3} from '../../src/modules/React360Proxy';
import {isMobile} from 'react-device-detect';

const getVectorMagnitude = ([...coordinates]) =>
    Math.sqrt([...coordinates].reduce((sum, currentValue) => sum + currentValue * currentValue, 0));

let _defaultCameraHeight = 1.3;
let _vectorScale = 0.005;
let _sphereRadius = 1000;

function setDefaultCameraHeight(defaultCameraHeight) {
    _defaultCameraHeight = defaultCameraHeight;
}

function setVectorScale(vectorScale) {
    _vectorScale = vectorScale;
}

function setSphereRadius(sphereRadius) {
    _sphereRadius = sphereRadius;
}

// validate hotspotData exists and does not consist of a single self referential hotspot
function validateHotspotData(hotspotData) {
    return (
        hotspotData &&
        hotspotData.length &&
        (hotspotData.length > 1 || hotspotData[0].initialVectorMagnitude > 0)
    );
}

function getHotspotData(sphereData, tourData, hideHotspots, hotspotRadius = HOTSPOT.DEFAULT_SCALE) {
    if (hideHotspots) {
        return [];
    }

    const sphereDataCameraPosition = sphereData.camera.position;
    /**
     *  Our sphere is an ellipse with hSphereRadius as a horizontal radius
     *  (1000 is default) and verticalRadius is 800 by default (just experimentally)
     *
     *  Additionally, we want to restrict hot spot locations to the front half of this
     *  viewable area to allow for hint regions to be drawn behind all hotspots.
     */
    const hSphereRadiusBound = _sphereRadius / 20; // TODO: What should the denominator here be so that very close hotspots still have hintregions displayed within the sphere.
    const vSphereRadiusBound = 0.8 * hSphereRadiusBound;
    if (!sphereData) {
        return [];
    }
    return (sphereData.hotspots || []).map((hotspot) => {
        const hotspotSphere = getSphereById(tourData, hotspot.sphereId);
        const hotSpotCameraPosition = hotspotSphere.camera.position;
        let hotspotCameraHeight;
        if (hotspotSphere.camera && hotspotSphere.camera.cameraFloorDistance) {
            hotspotCameraHeight = hotspotSphere.camera.cameraFloorDistance;
        } else {
            hotspotCameraHeight = _defaultCameraHeight;
        }
        let deltaX = (hotSpotCameraPosition.x - sphereDataCameraPosition.x) / _vectorScale;
        let deltaZ = (hotSpotCameraPosition.z - sphereDataCameraPosition.z) / _vectorScale;
        let deltaY =
            (hotSpotCameraPosition.y - sphereDataCameraPosition.y - hotspotCameraHeight) /
            _vectorScale;
        let vectorMagnitude = getVectorMagnitude([deltaX, deltaY, deltaZ]);
        const theta = Math.PI - Math.abs(Math.asin(deltaY / vectorMagnitude));

        const hotspotSphereRadius =
            (hSphereRadiusBound * vSphereRadiusBound) /
            Math.sqrt(
                vSphereRadiusBound * vSphereRadiusBound * Math.cos(theta) * Math.cos(theta) +
                    hSphereRadiusBound * hSphereRadiusBound * Math.sin(theta) * Math.sin(theta)
            );
        const thresholdRadius = Math.floor(
            hotspotRadius * Math.cos(theta) +
                Math.sqrt(
                    hotspotSphereRadius * hotspotSphereRadius -
                        hotspotRadius * hotspotRadius * Math.cos(theta) * Math.cos(theta)
                )
        );

        /* Since hotspots are cylinder shaped spots and when we scale the distance to them, we locate their centers on the
    edge of the sphere, if they are located this way, we will see only a half of the hotspot, so we need to make a
    step inside from the edge to the center of the sphere to fit the whole hotspot inside the sphere
    Also We see troubles with rendering items on the very edge, so we also add few pixels as a margin
     */
        let hotspotScaleCoefficient = 1;
        let initialVectorMagnitude = vectorMagnitude;
        if (vectorMagnitude > thresholdRadius) {
            hotspotScaleCoefficient = thresholdRadius / vectorMagnitude;
            deltaX *= hotspotScaleCoefficient;
            deltaY *= hotspotScaleCoefficient;
            deltaZ *= hotspotScaleCoefficient;
            vectorMagnitude = vectorMagnitude * hotspotScaleCoefficient;
        }

        const XZmagnitude = getVectorMagnitude([deltaX, deltaZ]);
        const desiredXZmagnitudeForHintRegion = XZmagnitude + hSphereRadiusBound;
        const hintRegionVectorCoefficient = desiredXZmagnitudeForHintRegion / XZmagnitude;
        const hintRegionDepthOffset = vectorMagnitude * hintRegionVectorCoefficient;

        return {
            coordinates: [deltaX, deltaY, deltaZ],
            image: hotspotSphere.image,
            id: hotspotSphere.sphereId,
            initialVectorMagnitude,
            scaleCoefficient: hotspotScaleCoefficient,
            hintRegionDepthOffset
        };
    });
}

/**
 * Give a set of hotspots, return the degrees needed to rotate around the Y-axis
 * to face the camera towards the majority of those hotspots.
 * @param hotspotData
 * @returns {number} in degrees
 */
function getRotationToTheMajorityOfHotspots(hotspotData) {
    if (validateHotspotData(hotspotData)) {
        let sumCoordinatesObject = hotspotData.reduce(
            ({x, z}, currentValue) => {
                let currentMagnitudeXZProjection = getVectorMagnitude([
                    currentValue.coordinates[0],
                    currentValue.coordinates[2]
                ]);

                // ignore vectors of length 0
                if (currentMagnitudeXZProjection === 0) {
                    return {x, z};
                }

                return {
                    x: x + currentValue.coordinates[0] / currentMagnitudeXZProjection,
                    z: z + currentValue.coordinates[2] / currentMagnitudeXZProjection
                };
            },
            {x: 0, z: 0}
        );
        let {x, z} = sumCoordinatesObject;
        const abs = getVectorMagnitude([x, z]);
        const theta = (Math.sign(z) * Math.acos(x / abs) * 180) / Math.PI;
        return -(90 + theta);
    }
    return 0;
}

/**
 * Give a set of hotspots, return the degrees needed to rotate around the Y-axis
 * to face the camera towards the nearest of those hotspots.
 * @param hotspotData
 * @returns {number} in degrees
 */
function getRotationToTheNearestHotspot(hotspotData) {
    if (validateHotspotData(hotspotData)) {
        let closestHotspotIndex = 0;
        let minDistance =
            hotspotData[0].initialVectorMagnitude > 0
                ? hotspotData[0].initialVectorMagnitude
                : Math.max;
        hotspotData.reduce((min, currentValue, currentIndex) => {
            let current = currentValue.initialVectorMagnitude;
            if (current !== 0 && current < min) {
                closestHotspotIndex = currentIndex;
                return current;
            }
            return min;
        }, minDistance);
        const closestHotspot = hotspotData[closestHotspotIndex].coordinates;
        const x = closestHotspot[0];
        const z = closestHotspot[2];
        const abs = getVectorMagnitude([x, 0, z]);
        const theta = (Math.sign(z) * Math.acos(x / abs) * 180) / Math.PI;
        return -(90 + theta);
    }
    return 0;
}

/**
 * Given the direct navigation data, hotspot data, and current sphere id,
 * will return a list of hotspots only in the current space.
 */
function getHotspotsInCurrentSpace(navigation, hotspots, sphereId) {
    let currentSpace = navigation.spaces.find((space) => space.landingSphere.id === sphereId);
    let sphereIdsInCurrentSpace = currentSpace.spheres.map((sphere) => sphere.id);
    let hotspotsInCurrentSpace = hotspots.filter((hotspot) =>
        sphereIdsInCurrentSpace.includes(hotspot.id)
    );
    return hotspotsInCurrentSpace;
}

/**
 * Given hotspot data and whether the viewer should face the hotspots,
 * will retun optimal heading calculations for the viewport to use
 */
function getViewportCalculations(hotspotData, faceTowardsHotspots) {
    let rotation = isMobile
        ? getRotationToTheNearestHotspot(hotspotData)
        : getRotationToTheMajorityOfHotspots(hotspotData);
    let point = faceTowardsHotspots ? new Vector3(0, 0, -1) : new Vector3(0, 0, 1);
    let euler = new Euler(0, (rotation * Math.PI) / 180, 0);
    point.applyEuler(euler);
    let result = point.toArray();
    return result;
}

/**
 * Uses hotspot data, direct navigation data, current sphere id,
 * and which navigation method was used, to determine the
 * optimal heading calculations that the viewport should use
 */
function getOptimalHeading(hotspots, directNav, currentSphereId, navigationMethod) {
    let faceHotspots = true; // default: will face hotspots

    // logic to determine correct heading for Direct Navigation
    if (navigationMethod === EdapEventMethods.spaceClick) {
        let hotspotsInCurrentSpace = getHotspotsInCurrentSpace(
            directNav,
            hotspots,
            currentSphereId
        );
        if (hotspotsInCurrentSpace.length >= 1) {
            // >=1 hotspot in space: only use same space hotspots
            hotspots = hotspotsInCurrentSpace;
        } else {
            // 0 hotspots in space: do not face them (do 180 flip)
            faceHotspots = false;
        }
    }

    let optimalHeading = getViewportCalculations(hotspots, faceHotspots);
    return optimalHeading;
}

export {
    getHotspotData,
    getHotspotsInCurrentSpace,
    getOptimalHeading,
    getRotationToTheMajorityOfHotspots,
    getRotationToTheNearestHotspot,
    getVectorMagnitude,
    getViewportCalculations,
    setDefaultCameraHeight,
    setSphereRadius,
    setVectorScale
};
